import React, { createContext, useContext, useState, useEffect } from 'react';
import { getMeetings, getMeetingAgendas } from '../services/api';

const MeetingContext = createContext();

/**
 * Sort agendas numerically by their hierarchical structure.
 * For example: 2, 3, 4.1, 6.1, 6.5, 6.6, 8.11, 9.11.2, 19.1.2, 20.6.1.3, etc.
 * 
 * @param {Array} agendas - Array of agenda items (can be strings or objects with agenda_item property)
 * @returns {Array} Sorted array of agendas
 */
const sortAgendas = (agendas) => {
  return [...agendas].sort((a, b) => {
    // Extract the agenda item string
    const agendaA = typeof a === 'string' ? a : a.agenda_item;
    const agendaB = typeof b === 'string' ? b : b.agenda_item;
    
    // Split by decimal point and convert to numbers
    const partsA = agendaA.split('.').map(part => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });
    const partsB = agendaB.split('.').map(part => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });
    
    // Compare each level of the hierarchy
    const maxLength = Math.max(partsA.length, partsB.length);
    for (let i = 0; i < maxLength; i++) {
      const numA = partsA[i] || 0;
      const numB = partsB[i] || 0;
      
      if (numA !== numB) {
        return numA - numB;
      }
    }
    
    // If all parts are equal, maintain original order
    return 0;
  });
};

export const useMeeting = () => {
  const context = useContext(MeetingContext);
  if (!context) {
    throw new Error('useMeeting must be used within a MeetingProvider');
  }
  return context;
};

export const MeetingProvider = ({ children }) => {
  const [selectedMeeting, setSelectedMeeting] = useState('RAN2#128');
  const [selectedAgenda, setSelectedAgenda] = useState('8.2.3');
  const [availableMeetings, setAvailableMeetings] = useState([]);
  const [availableAgendas, setAvailableAgendas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);

  // Load available meetings on mount
  useEffect(() => {
    const loadMeetings = async () => {
      try {
        const meetings = await getMeetings();
        setAvailableMeetings(meetings);
        
        // Check if there's a stored meeting preference
        const storedMeeting = localStorage.getItem('selected_meeting');
        const storedAgenda = localStorage.getItem('selected_agenda');
        
        if (storedMeeting && meetings.includes(storedMeeting)) {
          setSelectedMeeting(storedMeeting);
          if (storedAgenda) {
            setSelectedAgenda(storedAgenda);
          }
        } else if (meetings.length > 0) {
          // Default to first meeting if no stored preference
          setSelectedMeeting(meetings[0]);
        }
      } catch (error) {
        console.error('Error loading meetings:', error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    loadMeetings();
  }, []);

  // Load available agendas when meeting changes
  useEffect(() => {
    const loadAgendas = async () => {
      if (!selectedMeeting) return;
      
      try {
        const agendas = await getMeetingAgendas(selectedMeeting);
        
        // Sort agendas numerically before storing
        const sortedAgendas = sortAgendas(agendas);
        setAvailableAgendas(sortedAgendas);
        
        // If current agenda is not in the list or list is empty, reset to first agenda
        const agendaItems = sortedAgendas.map(a => typeof a === 'string' ? a : a.agenda_item);
        
        if (sortedAgendas.length === 0) {
          // No agendas available (all filtered out)
          setSelectedAgenda(null);
        } else if (!agendaItems.includes(selectedAgenda)) {
          // Current agenda not in list, select first available
          const firstAgenda = typeof sortedAgendas[0] === 'string' ? sortedAgendas[0] : sortedAgendas[0].agenda_item;
          setSelectedAgenda(firstAgenda);
        }
      } catch (error) {
        console.error('Error loading agendas:', error);
        setAvailableAgendas([]);
        setSelectedAgenda(null);
      }
    };

    loadAgendas();
  }, [selectedMeeting]); // Only depend on selectedMeeting

  // Handle meeting change
  const handleMeetingChange = (meeting) => {
    setSelectedMeeting(meeting);
    localStorage.setItem('selected_meeting', meeting);
    // Clear chat when meeting changes
    setChatMessages([]);
    setChatOpen(false);
    // Agenda will be reset in the useEffect above
  };

  // Handle agenda change
  const handleAgendaChange = (agenda) => {
    setSelectedAgenda(agenda);
    localStorage.setItem('selected_agenda', agenda);
    // Clear chat when agenda changes
    setChatMessages([]);
    setChatOpen(false);
  };

  const value = {
    selectedMeeting,
    selectedAgenda,
    availableMeetings,
    availableAgendas,
    setMeeting: handleMeetingChange,
    setAgenda: handleAgendaChange,
    loading,
    // Chat state
    chatOpen,
    setChatOpen,
    chatMessages,
    setChatMessages,
  };

  return (
    <MeetingContext.Provider value={value}>
      {children}
    </MeetingContext.Provider>
  );
};

